/* eslint-disable modernloop/restrict-imports.cjs */
import React from 'react';

import { logError } from '@modernloop/shared/utils';

import HelpCenterUrl from 'src/constants/HelpCenterUrl';

import LeverCreateCandidatePermissionDialog from 'src/views-new/JobDetailsPage/SourcingLinks/AtsCreateCandidatePermissionDialogs/LeverCreateCandidatePermissionDialog';
import LeverCard, { Props as LeverCardProps } from 'src/views-new/Settings/Integrations/Cards/LeverCard';

import { ListIntegrationResponse } from '../api/integration/useIntegrations';

import AtsSevice, {
  ExtensionSidebarParsedUrlType,
  ParsedAtsUrlResult,
  ScorecardGroupByStrategy,
  TScorecardGroupByKey,
  TScorecardSortKey,
} from './AtsService';

/* *
 * Lever ATS service
 * Imp: Never have atsType set in ATS service
 * */

class LeverAtsService implements AtsSevice {
  readonly featureFlag = null;

  readonly name = 'Lever';

  readonly hostname = 'lever'; // ex. https://hire.lever.co/

  readonly logoUrl = `/static/images/integrations/lever.png`;

  readonly helpCenterUrl = HelpCenterUrl.LEVER_INTEGRATION;

  readonly scorecardLabelPluralTranslationKey = 'hiring-dashboard-common:common.ats.feedbackForms';

  readonly noScorecardLabelTranslationKey = 'hiring-dashboard-common:common.ats.noFeedbackForm';

  readonly scorecardSelectTitleTranslationKey = 'hiring-dashboard-nav:nav.scorecards.selectTitle';

  readonly scorecardSearchPlaceholderTranslationKey = 'hiring-dashboard-nav:nav.scorecards.searchPlaceholder';

  readonly scorecardSearchPlaceholderEllipsisTranslationKey =
    'hiring-dashboard-nav:nav.scorecards.searchPlaceholderEllipsis';

  readonly isSourcingLinkEnabled = true;

  readonly isAtsUserSelectEnabled = true;

  readonly isAtsImportAvailabilityEnabled = false;

  readonly skipInterviewKitLinkTokenValidation = true;

  readonly scorecardSortKey: TScorecardSortKey = 'groupId';

  readonly scorecardGroupByStrategy = ScorecardGroupByStrategy.AtsField;

  readonly scorecardGroupByKey: TScorecardGroupByKey = 'groupName';

  readonly showEstimatedMinutes = false;

  readonly createCandidatePermissionDialog = LeverCreateCandidatePermissionDialog;

  readonly showRequisitionId = false;

  readonly canSupportBrightHire = false;

  readonly hasScorecardSync = false;

  readonly scorecardSyncSettingLabelTranslationKey =
    'hiring-dashboard-common:common.ats.keepInterviewsInSyncWithAtsInterviewPlan';

  readonly hasHiringProcess = false;

  readonly hasScorecards = true;

  readonly hasEnforcedJobPermissions = true;

  readonly resetInterviewPlanWarningMessage =
    'The interview plan for this stage will be reset to its current settings in Lever.';

  // eslint-disable-next-line class-methods-use-this
  getIntegrationCard = (props: LeverCardProps, integrations: ListIntegrationResponse): JSX.Element => {
    return <LeverCard {...props} lever={integrations?.lever} />;
  };

  parseExtensionSidebarUrl = (externalUrl: string): ParsedAtsUrlResult | null => {
    try {
      const url: URL | undefined = new URL(externalUrl);

      // check if url is lever
      if (!url.hostname.includes(this.hostname)) {
        return null;
      }

      const hasLeverOpportunityKeyword = (haystack: string) => {
        const keywords = ['candidates', 'search', 'inbox'];
        return keywords.some((keyword) => haystack.includes(keyword));
      };
      // check if url has candidates, search, or inbox in path
      if (hasLeverOpportunityKeyword(url.pathname)) {
        // get id from path, make sure it doesn't have candidates, search, or inbox in it
        const pathParts = url.pathname.split('/');
        const leverOpportunityId = pathParts.length > 2 ? pathParts[2] : null;
        if (leverOpportunityId && !hasLeverOpportunityKeyword(leverOpportunityId)) {
          return {
            type: ExtensionSidebarParsedUrlType.Candidate,
            atsCandidateId: leverOpportunityId,
            atsApplicationId: null,
          };
        }
      }
    } catch (error) {
      logError('Error parsing Lever ATS url', error);
    }
    return null;
  };
}

export default new LeverAtsService();
